/**
 * Apollo client provider. The frontend talks to the Rust GraphQL API at
 * same-origin `/graphql` routes by default. Vite proxies them during
 * development and the production web container proxies them to the api.
 * `VITE_GRAPHQL_HTTP` / `VITE_GRAPHQL_WS` remain available for split-origin
 * deployments.
 *
 * Auth is cookie-based: the officer logs in via `/auth/discord` (OAuth2), which
 * sets an httpOnly session cookie. Every request carries it because the links
 * use `credentials: 'include'`.
 *
 * Discord snowflake handling: Discord guild/user ids are 64-bit unsigned ints
 * (`Number.MAX_SAFE_INTEGER === 2^53 - 1 ≈ 9.0e15`; current snowflakes already
 * exceed `1e17`). JS `Number` cannot represent them precisely, so we keep
 * them as **strings** end-to-end on the client and let the custom `fetch`
 * below rewrite snowflakes in BOTH directions:
 *
 *   - **Request body**: string-form snowflake variables (`"guildId":"…274"`)
 *     become bare JSON numeric literals (`"guildId":…274`). async-graphql's
 *     `Int!` parser rejects strings, but accepts JSON numbers of any size —
 *     JSON itself has no precision cap, only JS `Number` does.
 *
 *   - **Response body**: JSON numeric fields named `discordGuildId` /
 *     `discordUserId` / `ownerDiscordId` / `officerRoleDiscordId` with 16+
 *     digits become JSON strings before Apollo parses the body. Without this,
 *     `JSON.parse` silently
 *     truncates real snowflakes (`565610609283039274 → 565610609283039300`),
 *     the wrong id reaches the resolver, and the api returns
 *     "guild not registered" / "not a member of this guild" against an id
 *     that has no DB row.
 *
 * The rewriters live in `./snowflakes.ts` so they're independently testable
 * (see `snowflakes.test.ts` — `npm test`).
 */
import { ApolloClient, HttpLink, InMemoryCache, split } from '@apollo/client/core'
import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient as createWsClient } from 'graphql-ws'

import { rewriteSnowflakes, rewriteSnowflakesInResponse } from './snowflakes'

// The api serves subscriptions at `/graphql/ws` (a `route_service` with the
// axum `GraphQLSubscription` handler), NOT `/graphql` — that route only has
// GET (playground) + POST (handler), so a WS upgrade against it is rejected
// and `graphql-ws`'s `shouldRetry` would otherwise reconnect forever. See
// `apps/backend/api/src/main.rs` `.route_service("/graphql/ws", ...)`.
function sameOriginWsUri(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/graphql/ws`
}

const httpUri = import.meta.env.DEV
  ? '/graphql'
  : import.meta.env['VITE_GRAPHQL_HTTP'] ?? '/graphql'
const wsUri = import.meta.env.DEV
  ? sameOriginWsUri()
  : import.meta.env['VITE_GRAPHQL_WS'] ?? sameOriginWsUri()

const httpLink = new HttpLink({
  uri: httpUri,
  // Send the httpOnly session cookie with every request.
  credentials: 'include',
  // Intercept both the serialised request body (snowflake variables
  // → numeric literals) and the response body (snowflake numeric fields
  // → JSON strings) before Apollo's cache sees either side. Apollo's
  // HttpLink calls the request side with `(input, init)` where `init.body`
  // is a JSON string of `{ operationName, variables, query }`.
  fetch: async (input, init) => {
    const nextInit =
      init && typeof init.body === 'string'
        ? { ...init, body: rewriteSnowflakes(init.body) }
        : init
    const response = await fetch(input, nextInit)
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('application/graphql') && !contentType.includes('application/json')) {
      return response
    }
    // Read the body once, then hand Apollo a fresh `Response` wrapping the
    // cached text. We MUST NOT return the original `response` after calling
    // `.text()` on it — the body stream is single-use, and Apollo's link
    // pipeline calls `.json()` on whatever we return, which would then throw
    // `Failed to execute 'text' on 'Response': body stream already read`.
    const original = await response.text()
    const rewritten = rewriteSnowflakesInResponse(original)
    return new Response(rewritten, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    })
  },
})

const wsLink = new GraphQLWsLink(
  createWsClient({
    url: wsUri,
    // `lazy: true` defers opening the underlying WebSocket until a
    // subscription is actually created and `subscribe()` is called. The board
    // subscription in `gvgPlannerStore` is only created once `dbGuildId` is
    // resolved, so the WS no longer opens (and fails) eagerly at app boot in
    // dev environments where the backend isn't running.
    lazy: true,
    // The session cookie isn't sent over WS by `graphql-ws`: the browser
    // doesn't expose httpOnly cookies to JS-side `WebSocket.send`, and this
    // client doesn't try to relay them via `connectionParams`. The browser
    // includes the same-site session cookie in the WebSocket upgrade, and
    // the API's subscription resolver authorizes it server-side. `shouldRetry`
    // keeps the client's built-in backoff alive — when the backend comes up
    // later (or the network flaps) the WS reconnects on its own, and the
    // subscription's `error` handler in the store swallows the resulting
    // transient errors so the UI keeps working off HTTP.
    shouldRetry: () => true,
  }),
)

// Route subscriptions to the WS link, everything else to HTTP.
const link = split(
  ({ query }) => {
    const def = getMainDefinition(query)
    return def.kind === 'OperationDefinition' && def.operation === 'subscription'
  },
  wsLink,
  httpLink,
)

export const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  // The Apollo Client DevTools browser extension isn't used in this project.
  // When the extension is **not** installed Apollo still prints an internal
  // banner via `console.info` on load — disabling `devtools.enabled` silences
  // that banner. Apollo Client 3.14 deprecated the legacy `connectToDevTools`
  // boolean (the construction-time warning steers consumers to the nested
  // `devtools.enabled` form). The typed key on `ApolloClientOptions` is
  // `devtools: DevtoolsOptions` — see `@apollo/client/core` 3.14.
  devtools: { enabled: false },
  // Cache config is **deliberate**, not an afterthought:
  //   - `query` defaults to `cache-first`: an in-cache query returns
  //     immediately, and the same call re-runs only when explicitly forced
  //     (`fetchPolicy: 'network-only'`) by the store. The four
  //     `apolloClient.query({ fetchPolicy: 'network-only' })` call sites in
  //     `gvgPlannerStore` / `OfficerManagementPanel` deliberately bypass the
  //     cache because they need authoritative, just-written state.
  //   - `watchQuery` defaults to `cache-and-network`: a watched query
  //     renders from cache first (so the UI never flashes empty), then
  //     updates once the network response arrives.
  //   - `mutation` / `subscription` use Apollo's built-in defaults.
  // seaography's Relay connections don't auto-tag nodes with `__typename`,
  // so entity-level normalisation across queries is limited to whatever the
  // response documents already cache. Good enough for v1.
  defaultOptions: {
    query: { fetchPolicy: 'cache-first' },
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
})

/**
 * OAuth2 entry: redirect to the api's Discord authorize endpoint for a guild.
 * `guildId` is the Discord snowflake — passed as a string to preserve
 * precision through JS's `Number` limit (see file header).
 *
 * Uses the Vite `/auth` proxy while developing. Discord's callback remains
 * registered against the backend URL; the proxy only fronts browser-initiated
 * frontend requests.
 */
export function loginWithDiscord(guildId: string): void {
  const apiBase = import.meta.env.DEV
    ? ''
    : import.meta.env['VITE_API_BASE_URL'] ?? ''
  window.location.href = `${apiBase}/auth/discord?guild_id=${guildId}`
}
